import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { toast } from "sonner";
import {
  ArrowUpRight, ScrollText, Users, MapPin, ShieldCheck,
  Scale, HandHeart, Send, Loader2, Mail, Phone, MapPinned,
  HandCoins, Smartphone, Landmark, Heart,
} from "lucide-react";
import Header from "@/components/Header";
import { Reveal, MaskedLine } from "@/components/Reveal";
import api, { formatApiErrorDetail } from "@/lib/api";

const IMG = {
  hero: "https://images.unsplash.com/photo-1642670270173-86ab9a78388a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxhZnJpY2FuJTIwcnVyYWwlMjBmYXJtaW5nJTIwbGFuZHNjYXBlfGVufDB8fHx8MTc4Nzg4MjgxN3ww&ixlib=rb-4.1.0&q=85",
  soil1: "https://images.unsplash.com/photo-1693414854278-6b3703411629?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxoYW5kcyUyMGhvbGRpbmclMjByaWNoJTIwc29pbCUyMGVhcnRofGVufDB8fHx8MTc4Nzg4MjgxN3ww&ixlib=rb-4.1.0&q=85",
  soil2: "https://images.unsplash.com/photo-1642952273588-ed6fa28870ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxoYW5kcyUyMGhvbGRpbmclMjByaWNoJTIwc29pbCUyMGVhcnRofGVufDB8fHx8MTc4Nzg4MjgxN3ww&ixlib=rb-4.1.0&q=85",
  map: "https://images.unsplash.com/photo-1772536888848-c0e7f0f6cf39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMHRvcG9ncmFwaHklMjBtYXAlMjB0ZXh0dXJlfGVufDB8fHx8MTc4Nzg4MjgxN3ww&ixlib=rb-4.1.0&q=85",
};

const chapters = [
  { n: "01", t: "Documenter la terre", icon: ScrollText,
    d: "Nous recensons et cartographions les parcelles rurales pour donner à chaque communauté une preuve tangible de son patrimoine foncier." },
  { n: "02", t: "Sécuriser les droits", icon: ShieldCheck,
    d: "Face à l'accaparement des terres, nous accompagnons les familles vers la reconnaissance légale de leurs droits coutumiers et fonciers." },
  { n: "03", t: "Défendre l'héritage", icon: Scale,
    d: "Nous portons la voix des paysans dans les litiges fonciers et militons pour une gouvernance juste et transparente du territoire." },
];

const actions = [
  { title: "Cartographie participative", img: IMG.map, tag: "Terrain",
    d: "Relevés GPS et registres communautaires des parcelles rurales." },
  { title: "Médiation foncière", img: IMG.soil2, tag: "Justice",
    d: "Résolution pacifique des conflits de terre entre familles et villages." },
  { title: "Agriculture durable", img: IMG.soil1, tag: "Avenir",
    d: "Protéger les sols nourriciers pour les générations à venir." },
];

const stats = [
  { v: "3 200+", l: "Hectares recensés" },
  { v: "48", l: "Villages accompagnés" },
  { v: "1 100", l: "Familles sécurisées" },
  { v: "12 ans", l: "Sur le terrain" },
];

export default function Landing() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <div className="relative bg-background text-foreground overflow-x-hidden">
      <Header />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden">
        <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
          <img src={IMG.hero} alt="Paysage rural africain" className="w-full h-[115%] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[hsl(120_38%_10%)] via-[hsl(120_38%_10%)]/40 to-[hsl(120_38%_10%)]/20" />
        </motion.div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 w-full pb-20 pt-40">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="text-[hsl(45_45%_97%)]/80 text-xs tracking-[0.25em] uppercase font-bold">
              Patrimoine foncier rural
            </span>
            <span className="h-px w-16 bg-[hsl(16_57%_50%)]" />
          </motion.div>

          <h1 className="font-heading text-[hsl(45_45%_97%)] font-semibold tracking-tighter leading-[0.92] text-5xl sm:text-7xl lg:text-8xl max-w-5xl">
            <MaskedLine delay={0.15}>La terre n'est pas</MaskedLine>
            <MaskedLine delay={0.32} className="italic text-[hsl(16_57%_60%)]">à vendre.</MaskedLine>
            <MaskedLine delay={0.49}>Elle se transmet.</MaskedLine>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.8 }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-end gap-8 justify-between"
          >
            <p className="max-w-md text-[hsl(45_45%_97%)]/80 text-base leading-relaxed">
              ONG HERITAGES protège le patrimoine foncier des communautés rurales :
              cartographier, sécuriser et transmettre la terre de nos ancêtres.
            </p>
            <a
              href="#adhesion"
              data-testid="hero-cta"
              className="group shrink-0 inline-flex items-center gap-3 px-7 py-4 rounded-full bg-[hsl(16_57%_50%)] text-[hsl(45_45%_97%)] font-medium hover:-translate-y-1 transition-transform"
            >
              Défendre notre terre
              <ArrowUpRight className="w-5 h-5 group-hover:rotate-45 transition-transform" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* MISSION / MANIFESTO */}
      <section id="mission" className="relative py-28 lg:py-40">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-8 items-end mb-20">
            <Reveal className="lg:col-span-7">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-secondary">Le Manifeste</span>
              <h2 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95] text-primary">
                Trois combats pour un même héritage
              </h2>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-4 lg:col-start-9">
              <p className="text-muted-foreground leading-relaxed">
                Dans nos campagnes, la terre est mémoire, identité et survie. Nous agissons
                sur trois fronts indissociables pour la préserver.
              </p>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-border rounded-3xl overflow-hidden border border-border">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.12}>
                <div className="group h-full bg-card p-10 lg:p-12 flex flex-col hover:bg-primary transition-colors duration-500">
                  <div className="flex items-center justify-between mb-16">
                    <span className="font-heading text-5xl text-secondary group-hover:text-[hsl(16_57%_60%)]">{c.n}</span>
                    <c.icon className="w-7 h-7 text-primary group-hover:text-primary-foreground transition-colors" />
                  </div>
                  <h3 className="font-heading text-2xl lg:text-3xl text-primary group-hover:text-primary-foreground transition-colors mb-4">
                    {c.t}
                  </h3>
                  <p className="text-muted-foreground group-hover:text-primary-foreground/80 leading-relaxed transition-colors">
                    {c.d}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-10 border-y border-border bg-card/50">
        <Marquee speed={40} gradient={false} autoFill>
          {["Terre", "Mémoire", "Justice", "Héritage", "Communauté", "Souveraineté"].map((w, i) => (
            <span key={i} className="mx-12 font-heading italic text-5xl lg:text-7xl text-primary/25">
              {w} <span className="text-secondary/40 not-italic">✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* ACTIONS */}
      <section id="actions" className="py-28 lg:py-40">
        <div className="max-w-[1400px] mx-auto px-6">
          <Reveal>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-secondary">Nos Actions</span>
            <h2 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-primary max-w-3xl leading-[0.95]">
              Du terrain jusqu'aux tribunaux
            </h2>
          </Reveal>

          <div className="mt-16 grid md:grid-cols-3 gap-8">
            {actions.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.12}>
                <div className="group cursor-default">
                  <div className="relative overflow-hidden rounded-[1.5rem] aspect-[4/5]">
                    <img
                      src={a.img} alt={a.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/70 to-transparent" />
                    <span className="absolute top-5 left-5 px-3 py-1 rounded-full bg-background/85 text-xs font-bold uppercase tracking-widest text-primary">
                      {a.tag}
                    </span>
                  </div>
                  <h3 className="mt-6 font-heading text-2xl text-primary flex items-center gap-2">
                    {a.title}
                    <ArrowUpRight className="w-5 h-5 text-secondary opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                  </h3>
                  <p className="mt-2 text-muted-foreground leading-relaxed">{a.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* IMPACT */}
      <section id="impact" className="relative py-28 lg:py-40 bg-primary text-primary-foreground overflow-hidden">
        <img src={IMG.map} alt="" className="absolute inset-0 w-full h-full object-cover opacity-10 mix-blend-luminosity" />
        <div className="relative max-w-[1400px] mx-auto px-6">
          <Reveal>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-2xl leading-[0.95]">
              Un héritage mesuré, hectare par hectare
            </h2>
          </Reveal>
          <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-primary-foreground/15 border border-primary-foreground/15 rounded-2xl overflow-hidden">
            {stats.map((s, i) => (
              <Reveal key={s.l} delay={i * 0.1}>
                <div className="bg-primary p-8 lg:p-10">
                  <div className="font-heading text-4xl lg:text-6xl text-[hsl(16_57%_60%)]">{s.v}</div>
                  <div className="mt-3 text-sm text-primary-foreground/70">{s.l}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <JoinContact />
      <DonSection />
      <Footer />
    </div>
  );
}

function JoinContact() {
  const [mLoading, setMLoading] = useState(false);
  const [cLoading, setCLoading] = useState(false);

  const submitMembership = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    setMLoading(true);
    try {
      const { data } = await api.post("/membership", {
        nom: f.get("nom"), email: f.get("email"), telephone: f.get("telephone"),
        localite: f.get("localite"), type: f.get("type") || "adhesion", message: f.get("message"),
      });
      toast.success(data.message);
      e.target.reset();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setMLoading(false); }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    setCLoading(true);
    try {
      const { data } = await api.post("/contact", {
        nom: f.get("nom"), email: f.get("email"), sujet: f.get("sujet"), message: f.get("message"),
      });
      toast.success(data.message);
      e.target.reset();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setCLoading(false); }
  };

  const input = "w-full bg-transparent border-b border-border py-3 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-secondary transition-colors";

  return (
    <section id="adhesion" className="py-28 lg:py-40">
      <div className="max-w-[1400px] mx-auto px-6 grid lg:grid-cols-12 gap-16">
        {/* Membership */}
        <Reveal className="lg:col-span-7">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-secondary flex items-center gap-2">
            <HandHeart className="w-4 h-4" /> Rejoindre le mouvement
          </span>
          <h2 className="mt-5 font-heading text-4xl sm:text-5xl tracking-tight text-primary leading-[0.95]">
            Devenez gardien de la terre
          </h2>
          <p className="mt-4 text-muted-foreground max-w-lg leading-relaxed">
            Adhérez, engagez-vous comme bénévole ou nouez un partenariat. Chaque main compte
            pour protéger notre patrimoine foncier.
          </p>

          <form onSubmit={submitMembership} data-testid="membership-form" className="mt-10 grid sm:grid-cols-2 gap-6">
            <input name="nom" required placeholder="Nom complet" data-testid="membership-nom" className={input} />
            <input name="email" type="email" required placeholder="Email" data-testid="membership-email" className={input} />
            <input name="telephone" placeholder="Téléphone" data-testid="membership-tel" className={input} />
            <input name="localite" placeholder="Village / Localité" data-testid="membership-localite" className={input} />
            <select name="type" data-testid="membership-type" className={input + " cursor-pointer"}>
              <option value="adhesion">Adhésion</option>
              <option value="benevolat">Bénévolat</option>
              <option value="partenariat">Partenariat</option>
            </select>
            <input name="message" placeholder="Un mot (facultatif)" className={input} />
            <button
              type="submit" data-testid="membership-submit" disabled={mLoading}
              className="sm:col-span-2 justify-self-start mt-4 inline-flex items-center gap-3 px-7 py-4 rounded-full bg-secondary text-secondary-foreground font-medium hover:-translate-y-1 transition-transform disabled:opacity-60"
            >
              {mLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              Envoyer ma demande
            </button>
          </form>
        </Reveal>

        {/* Contact */}
        <Reveal delay={0.15} className="lg:col-span-4 lg:col-start-9">
          <div id="contact" className="bg-card border border-border rounded-3xl p-8 lg:p-10">
            <h3 className="font-heading text-3xl text-primary">Nous écrire</h3>
            <form onSubmit={submitContact} data-testid="contact-form" className="mt-6 space-y-5">
              <input name="nom" required placeholder="Votre nom" data-testid="contact-nom" className={input} />
              <input name="email" type="email" required placeholder="Votre email" data-testid="contact-email" className={input} />
              <input name="sujet" required placeholder="Sujet" data-testid="contact-sujet" className={input} />
              <textarea name="message" required rows={3} placeholder="Votre message" data-testid="contact-message" className={input + " resize-none"} />
              <button
                type="submit" data-testid="contact-submit" disabled={cLoading}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary text-primary-foreground font-medium hover:-translate-y-1 transition-transform disabled:opacity-60"
              >
                {cLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Mail className="w-5 h-5" />}
                Envoyer
              </button>
            </form>
            <div className="mt-8 pt-6 border-t border-border space-y-3 text-sm text-muted-foreground">
              <p className="flex items-center gap-3"><Phone className="w-4 h-4 text-secondary" /> +226 00 00 00 00</p>
              <p className="flex items-center gap-3"><Mail className="w-4 h-4 text-secondary" /> contact@ongheritages.org</p>
              <p className="flex items-center gap-3"><MapPinned className="w-4 h-4 text-secondary" /> Afrique de l'Ouest — Zones rurales</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function DonSection() {
  const [loading, setLoading] = useState(false);
  const [preset, setPreset] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    setLoading(true);
    try {
      const { data } = await api.post("/donations", {
        nom: f.get("nom"), email: f.get("email"), telephone: f.get("telephone"),
        montant: parseFloat(f.get("montant")) || 0,
        frequence: f.get("frequence") || "ponctuel",
        moyen: f.get("moyen") || "mobile_money",
        message: f.get("message"),
      });
      toast.success(data.message);
      if (data.id) {
        try {
          const res = await api.get(`/donations/${data.id}/receipt`, { responseType: "blob" });
          const url = URL.createObjectURL(res.data);
          const a = document.createElement("a");
          a.href = url; a.download = `recu_don_${data.id.slice(-6).toUpperCase()}.pdf`; a.click();
          URL.revokeObjectURL(url);
          toast.success("Votre reçu de don a été téléchargé. Merci !");
        } catch { /* receipt optional */ }
      }
      e.target.reset();
      setPreset("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const input = "w-full bg-transparent border-b border-primary-foreground/25 py-3 text-primary-foreground placeholder:text-primary-foreground/50 focus:outline-none focus:border-[hsl(16_57%_60%)] transition-colors";
  const amounts = [5000, 10000, 25000, 50000];

  return (
    <section id="don" className="py-28 lg:py-40 bg-primary text-primary-foreground">
      <div className="max-w-[1400px] mx-auto px-6 grid lg:grid-cols-12 gap-16">
        <Reveal className="lg:col-span-5">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[hsl(16_57%_60%)] flex items-center gap-2">
            <Heart className="w-4 h-4" /> Soutenir la cause
          </span>
          <h2 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
            Chaque don enracine<br /><span className="italic text-[hsl(16_57%_60%)]">un avenir</span>
          </h2>
          <p className="mt-5 text-primary-foreground/80 max-w-md leading-relaxed">
            Votre générosité finance la cartographie des parcelles, la médiation foncière
            et la défense juridique des familles rurales.
          </p>

          <div className="mt-10 space-y-4">
            <p className="text-xs uppercase tracking-widest text-primary-foreground/50">Coordonnées de paiement</p>
            <div className="flex items-start gap-3">
              <Smartphone className="w-5 h-5 text-[hsl(16_57%_60%)] mt-0.5" />
              <div>
                <p className="font-medium">Mobile Money</p>
                <p className="text-primary-foreground/70 text-sm">+225 07 08 39 14 12 — ONG Heritages</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Landmark className="w-5 h-5 text-[hsl(16_57%_60%)] mt-0.5" />
              <div>
                <p className="font-medium">Virement bancaire</p>
                <p className="text-primary-foreground/70 text-sm">IBAN CI00 0000 0000 0000 — ONG HERITAGES</p>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-6 lg:col-start-7">
          <form onSubmit={submit} data-testid="donation-form" className="bg-primary-foreground/5 border border-primary-foreground/15 rounded-3xl p-8 lg:p-10">
            <h3 className="font-heading text-3xl mb-6">Faire un don</h3>

            <div className="grid grid-cols-4 gap-2 mb-2">
              {amounts.map((a) => (
                <button type="button" key={a} data-testid={`don-preset-${a}`}
                  onClick={() => setPreset(String(a))}
                  className={`py-2.5 rounded-lg text-sm border transition-colors ${
                    preset === String(a) ? "bg-[hsl(16_57%_50%)] border-[hsl(16_57%_50%)] text-white" : "border-primary-foreground/25 text-primary-foreground/80 hover:border-[hsl(16_57%_60%)]"
                  }`}>
                  {a.toLocaleString("fr-FR")}
                </button>
              ))}
            </div>
            <p className="text-xs text-primary-foreground/60 mb-6">
              Ou saisissez le <span className="text-[hsl(16_57%_60%)] font-medium">montant de votre choix</span> ci-dessous.
            </p>

            <div className="grid sm:grid-cols-2 gap-6">
              <input name="nom" required placeholder="Nom complet" data-testid="don-nom" className={input} />
              <input name="email" type="email" required placeholder="Email" data-testid="don-email" className={input} />
              <input name="telephone" placeholder="Téléphone" data-testid="don-tel" className={input} />
              <input name="montant" type="number" min="500" required value={preset}
                onChange={(e) => setPreset(e.target.value)}
                placeholder="Montant de votre choix (FCFA)" data-testid="don-montant" className={input} />
              <select name="frequence" data-testid="don-frequence" className={input + " cursor-pointer"}>
                <option className="text-black" value="ponctuel">Don ponctuel</option>
                <option className="text-black" value="mensuel">Don mensuel</option>
              </select>
              <select name="moyen" data-testid="don-moyen" className={input + " cursor-pointer"}>
                <option className="text-black" value="mobile_money">Mobile Money</option>
                <option className="text-black" value="virement">Virement</option>
                <option className="text-black" value="especes">Espèces</option>
              </select>
              <input name="message" placeholder="Un mot (facultatif)" className={input + " sm:col-span-2"} />
              <button type="submit" disabled={loading} data-testid="don-submit"
                className="sm:col-span-2 mt-2 inline-flex items-center justify-center gap-3 py-4 rounded-full bg-[hsl(16_57%_50%)] text-white font-medium hover:-translate-y-1 transition-transform disabled:opacity-60">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <HandCoins className="w-5 h-5" />}
                Je fais un don
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="max-w-[1400px] mx-auto px-6 py-16">
        <div className="flex flex-col lg:flex-row justify-between gap-10">
          <div>
            <div className="flex items-center gap-2.5">
              <img src="/logo.png" alt="ONG HERITAGES" className="w-11 h-11 rounded-full object-cover" />
              <span className="font-heading text-2xl font-semibold">ONG HERITAGES</span>
            </div>
            <p className="mt-4 max-w-sm text-primary-foreground/70 leading-relaxed">
              Pour un foncier rural juste, documenté et transmis aux générations futures.
            </p>
          </div>
          <div className="flex gap-16">
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-widest text-primary-foreground/50">Naviguer</p>
              <a href="#mission" className="block text-primary-foreground/80 hover:text-secondary transition-colors">Mission</a>
              <a href="#actions" className="block text-primary-foreground/80 hover:text-secondary transition-colors">Actions</a>
              <a href="#adhesion" className="block text-primary-foreground/80 hover:text-secondary transition-colors">Adhésion</a>
            </div>
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-widest text-primary-foreground/50">Membres</p>
              <a href="/connexion" className="block text-primary-foreground/80 hover:text-secondary transition-colors">Connexion</a>
              <a href="/registre" className="block text-primary-foreground/80 hover:text-secondary transition-colors">Registre foncier</a>
            </div>
          </div>
        </div>
        <div className="mt-14 pt-6 border-t border-primary-foreground/15 text-sm text-primary-foreground/50">
          © {new Date().getFullYear()} ONG HERITAGES — Tous droits réservés.
        </div>
      </div>
    </footer>
  );
}
